import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { PageHeader } from 'app/shared/components/page-header/PageHeader';
import SelectPaginate from 'app/shared/components/select-paginate/SelectPaginate';
import TireChassisStructure from 'app/shared/components/tire-chassis-structure/tire-chassis-structure';
import { ITireDTO } from 'app/shared/model/dto/tire-dto.model';
import { TireStatus } from 'app/shared/model/enumerations/tire-status.model';
import { ITireTruckImplement } from 'app/shared/model/tire-truck-implement.model';
import { convertDateTimeToPtBRWithoutHour } from 'app/shared/util/date-utils';
import { Loading } from 'app/shared/util/loading';
import React, { useEffect, useState } from 'react';
import { translate, Translate } from 'react-jhipster';
import { toast } from 'react-toastify';
import { RouteComponentProps } from 'react-router-dom';
import { BoxForm } from 'app/shared/layout/register/DefaultFormsComponents';
import { RegisterButton } from 'app/shared/layout/register/RegisterButton';
import TireDetail from '../component/tire-detail';
import TireImg from '../component/tire-img';
import { renderStatus } from '../component/tire-status';
import TireTableDetail from '../component/tire-table-detail';
import TireVehicleOdometerField from '../component/tire-vehicle-odometer-field';
import {
  getTireTruckImplementCustomByTruckImplementId,
  getTruckImplementNumberOfTires,
} from './tire-truck-implement-custom.reducer';
import { getTireLastOperation, reset as resetRotation, saveAllTireRotation } from './tire-rotation-custom.reducer';
import { isVehicleModelTireFunctionComplete } from '../util/tire-function-completeness';

export interface ITirePositions {
  position: number;
  steppe: boolean;
  selected: boolean;
}

const TireTruckImplementCustomUpdate = (props: RouteComponentProps<{ truckImplementId: string }>) => {
  const dispatch = useAppDispatch();

  const [selectedTire, setSelectedTire] = useState<ITireDTO | null>(null);
  const [tirePositions, setTirePositions] = useState<ITirePositions[]>([]);
  const [selectedPosition, setSelectedPosition] = useState<ITirePositions | null>(null);
  const [tableAddedTires, setTableAddedTires] = useState<ITireTruckImplement[]>([]);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [vehicleOdometer, setVehicleOdometer] = useState('');

  const loading = useAppSelector(state => state.tireTruckImplementCustom.loading);
  const updateSuccess = useAppSelector(state => state.tireRotationCustom.updateSuccess);
  const truckImplementTireDataDTO = useAppSelector(state => state.tireTruckImplementCustom.truckImplementTireDataDTO);
  const tireTruckImplements = useAppSelector(state => state.tireTruckImplementCustom.entities);
  const lastOperation = useAppSelector(state => state.tireRotationCustom.lastOperation);

  useEffect(() => {
    dispatch(resetRotation());
    dispatch(getTireTruckImplementCustomByTruckImplementId(props.match.params.truckImplementId));
    dispatch(getTruckImplementNumberOfTires(props.match.params.truckImplementId));
    dispatch(getTireLastOperation(Number(props.match.params.truckImplementId)));
  }, []);

  useEffect(() => {
    const dataPosition: ITirePositions[] = [];

    if (!dataPosition.length) {
      for (let i = 1; i <= truckImplementTireDataDTO.numberOfTires; i++) {
        dataPosition.push({ position: i, steppe: false, selected: true });
      }

      dataPosition.push({ position: dataPosition.length + 1, steppe: true, selected: true });
      dataPosition.push({ position: dataPosition.length + 1, steppe: true, selected: true });

      setTirePositions(dataPosition);
    }
  }, [truckImplementTireDataDTO]);

  useEffect(() => {
    if (updateSuccess) {
      props.history.push(`/dashboards/truck-implement/${props.match.params.truckImplementId}/tire`);
    }
  }, [updateSuccess]);

  const handleClose = () => {
    props.history.goBack();
  };

  const addingTiresToTable = () => {
    if (selectedTire && selectedPosition) {
      const tireTruckImplement = tireTruckImplements.find(tti => tti.tire?.marking === selectedTire.marking);
      const tireTI = [
        ...tableAddedTires,
        {
          id: tireTruckImplement ? tireTruckImplement.id : null,
          position: selectedPosition.position,
          steppe: selectedPosition.steppe,
          tire: { ...selectedTire, status: tireTruckImplements && tireTruckImplements.length > 0 ? TireStatus.ROTATION : TireStatus.INSTALLED },
          truckImplement: { id: truckImplementTireDataDTO.id, },
        },
      ] as ITireTruckImplement[];

      setTableAddedTires(tireTI);
      setSelectedTire(null);
      setSelectedPosition(null);
    }
  };

  const removeItemFromTable = index => {
    const updatedTableAddedTires = [...tableAddedTires];
    updatedTableAddedTires.splice(index, 1);
    setTableAddedTires(updatedTableAddedTires);
  };

  const mapTireData = () => {
    return tirePositions.map(tire => {
      const isAdded = tableAddedTires.some(item => item.position === tire.position);

      return {
        position: tire.position,
        steppe: tire.steppe,
        selected: isAdded ? true : false,
      };
    });
  };

  const isRotation = tireTruckImplements?.length > 0;

  const tireFunctionComplete = isVehicleModelTireFunctionComplete(
    truckImplementTireDataDTO?.vehicleModel,
    truckImplementTireDataDTO?.numberOfTires
  );

  const chassisTireCount = tirePositions.filter(position => !position.steppe).length;
  const rotatedTireCount = tableAddedTires.filter(tire => !tire.steppe).length;

  const saveEntity = () => {
    if (!tableAddedTires.length || !vehicleOdometer) {
      return;
    }

    const installOdometer = Number(vehicleOdometer);

    const payload = tableAddedTires.map(tti => ({
      ...tti,
      installOdometer,
    }));

    dispatch(saveAllTireRotation(payload));
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!tireFunctionComplete) {
      return toast.error(translate('fleetSenseApp.tireTruckImplement.incompleteVehicleModelError'));
    }

    if (!vehicleOdometer) {
      return toast.error('Insira o odômetro do veículo');
    }

    if (!tableAddedTires.length) {
      return toast.error('Adicione pelo menos um pneu a uma posição');
    }

    if (rotatedTireCount !== chassisTireCount) {
      setModalVisible(true);
      return;
    }

    saveEntity();
  };

  const handleCloseModal = () => setModalVisible(false);

  const renderTitle = `${tireTruckImplements?.length > 0 ? 'Rodízio de pneus' : 'Instalação de pneus'}`;

  const findPosition = (tire: ITireDTO) => {
    return tireTruckImplements.find(item => item.tire?.id === tire.id)?.position ?? undefined;
  };

  const renderModalValidateSave = () => (
    <>
      <BoxForm onSubmit={handleSubmit}>
        <RegisterButton onBack={handleClose} />
      </BoxForm>
      <Dialog open={modalVisible} onClose={handleCloseModal} fullWidth maxWidth="lg" PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight={600}>
            {renderTitle}
          </Typography>
          <IconButton onClick={handleCloseModal}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Typography sx={{ mb: 4 }}>Tem certeza que deseja salvar?</Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography>{renderTitle}</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2 }}>
                    <TireImg />
                    <Typography
                      variant="h2"
                      sx={{ ml: 3, color: rotatedTireCount < chassisTireCount ? 'error.main' : 'success.main' }}
                    >
                      {rotatedTireCount}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography>Pneus do chassi</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2 }}>
                    <TireImg />
                    <Typography variant="h2" sx={{ ml: 3 }}>
                      {chassisTireCount}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>
        <Divider />
        <Box sx={{ px: 3, py: 2 }}>
          <BoxForm
            onSubmit={event => {
              event.preventDefault();
              saveEntity();
            }}
          >
            <RegisterButton onBack={handleCloseModal} />
          </BoxForm>
        </Box>
      </Dialog>
    </>
  );

  return (
    <Box sx={{ backgroundColor: 'primary.main', borderRadius: 2, p: 2 }}>
      <PageHeader
        headingId="tire-association-rotation-heading"
        headingDataCy="tireAssociationRotationHeading"
        contentKey={isRotation ? 'fleetSenseApp.tireTruckImplement.home.rotationTitle' : 'fleetSenseApp.tireTruckImplement.home.associationTitle'}
        showButton={false}
        onBackClick={handleClose}
      />

      {truckImplementTireDataDTO ? (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12} md>
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="subtitle1" color="text.primary">
                        Placa <b>{truckImplementTireDataDTO?.licensePlate}</b>
                      </Typography>
                      <Typography variant="subtitle1" color="text.primary">
                        Chassi <b>{truckImplementTireDataDTO?.chassisNumber}</b>
                      </Typography>
                    </Box>
                    {lastOperation?.rotationCreated && (
                      <Typography variant="subtitle1" color="text.primary">
                        Último rodízio realizado: <b>{convertDateTimeToPtBRWithoutHour(lastOperation.rotationCreated)}</b>
                      </Typography>
                    )}
                  </Box>
                  {!tireFunctionComplete && (
                    <Alert severity="warning" sx={{ mb: 3 }}>
                      {translate('fleetSenseApp.tireTruckImplement.incompleteVehicleModelWarning')}
                    </Alert>
                  )}
                  <TireVehicleOdometerField truckImplementId={truckImplementTireDataDTO?.id ?? ''} value={vehicleOdometer} onChange={setVehicleOdometer} />
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                          <Typography component="label" display="block" sx={{ mb: 1 }}>
                            Selecione o pneu {tireTruckImplements?.length > 0 ? null : renderStatus(TireStatus.STOCK)}
                          </Typography>
                          <SelectPaginate
                            value={selectedTire}
                            apiUrl={`api/tires-dto${tireTruckImplements?.length > 0 ? '-truck' : ''}/${truckImplementTireDataDTO?.id}`}
                            onChange={setSelectedTire}
                            valueName="marking"
                            displayFormat={option => {
                              const position = findPosition(option);
                              return position != null ? `${option?.marking} - ${position}` : `${option?.marking}`;
                            }}
                            excludeOptionIds={tableAddedTires.map(item => item.tire?.id).filter((id): id is number => id != null)}
                            isRequired
                            menuPortalTarget={document.body}
                          />
                          {selectedTire && <TireDetail tire={selectedTire} position={findPosition(selectedTire)} steppe={selectedPosition?.steppe} />}
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                          <Typography component="label" display="block" sx={{ mb: 1 }}>
                            Selecione a posição do pneu
                          </Typography>
                          <SelectPaginate
                            value={selectedPosition ? { ...selectedPosition, id: selectedPosition.position } : null}
                            staticOptions={tirePositions.map(item => ({ ...item, id: item.position }))}
                            onChange={option => setSelectedPosition(option ? tirePositions.find(tp => tp.position === option.position) ?? null : null)}
                            displayFormat={option =>
                              `${option.position}${option.steppe ? ` (${translate(`fleetSenseApp.TireStatus.${TireStatus.STEPPE}`)})` : ''}`
                            }
                            excludeOptionIds={tableAddedTires.map(item => item.position).filter((position): position is number => position != null)}
                            menuPortalTarget={document.body}
                          />
                          {selectedPosition && (
                            <Box sx={{ mt: 3, textAlign: 'center' }}>
                              <TireImg position={selectedPosition?.position} steppe={selectedPosition?.steppe} />
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                  <Box sx={{ mt: 3, textAlign: 'right' }}>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<AddIcon />}
                      onClick={addingTiresToTable}
                      disabled={!tireFunctionComplete || !(selectedTire && selectedPosition)}
                    >
                      Instalar pneu
                    </Button>
                  </Box>
                </CardContent>
              </Card>
              <Grid container spacing={2} sx={{ mt: 4 }}>
                {tireTruckImplements && tireTruckImplements.length > 0 && (
                  <Grid item xs={12} md={6} sx={{ mb: 3 }}>
                    <Typography variant="h5" color="text.primary">Posição antiga dos pneus</Typography>
                    <Card sx={{ px: 2, height: '100%' }}>
                      <TireTableDetail tireTruckImplements={tireTruckImplements} compact />
                    </Card>
                  </Grid>
                )}
                <Grid item xs={12} md={6} sx={{ mb: 3 }}>
                  <Typography variant="h5" color="text.primary">
                    {tireTruckImplements && tireTruckImplements.length > 0 ? 'Nova posição dos pneus' : 'Instalação'}
                  </Typography>
                  <Card sx={{ px: 2, height: '100%' }}>
                    <TireTableDetail tireTruckImplements={tableAddedTires} onClick={removeItemFromTable} compact />
                  </Card>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md="auto">
              {tirePositions && (
                <Card>
                  <CardContent>
                    <Typography variant="h5" color="text.primary">Chassi</Typography>
                    <TireChassisStructure
                      vehicleType={truckImplementTireDataDTO?.vehicleModel?.type ?? ''}
                      tireData={mapTireData()}
                    />
                  </CardContent>
                </Card>
              )}
            </Grid>
          </Grid>
          <Box sx={{ mt: 3, textAlign: 'right' }}>{renderModalValidateSave()}</Box>
        </>
      ) : (
        loading && <Loading />
      )}
    </Box>
  );
};

export default TireTruckImplementCustomUpdate;
